import * as React from 'react';
import { alpha } from '@mui/material/styles';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import { toggleButtonClasses } from '@mui/material/ToggleButton';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { gray, brand } from '../themePrimitives';


export const inputsCustomizations = {
  MuiButtonBase: {
    defaultProps: {
      disableTouchRipple: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        boxSizing: 'border-box',
        transition: 'all 160ms ease',
        '&:focus-visible': {
          outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          outlineOffset: '2px',
        },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius:
        // Handles string and number conditions for theme.shape.borderRadius
        typeof theme.shape.borderRadius === 'number'
          ? `calc(${theme.shape.borderRadius}px - 2px)`
          : `calc(${theme.shape.borderRadius} - 2px)`,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(15),
        lineHeight: 1.15,
        letterSpacing: 0,
        whiteSpace: 'nowrap',
        paddingInline: '1.1rem',
        transition:
          'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              height: '2.25rem',
              padding: '8px 14px',
              fontSize: theme.typography.pxToRem(14),
            },
          },
          {
            props: {
              size: 'medium',
            },
            style: {
              height: '2.75rem',
              padding: '10px 18px',
              fontSize: theme.typography.pxToRem(15),
            },
          },
          {
            props: {
              color: 'primary',
              variant: 'contained',
            },
            style: {
              color: gray[50],
              backgroundColor: brand[500],
              backgroundImage: `linear-gradient(135deg, ${brand[400]} 0%, ${brand[600]} 100%)`,
              boxShadow: `0 12px 24px ${alpha(brand[500], 0.22)}`,
              border: `1px solid ${alpha(brand[700], 0.4)}`,
              '&:hover': {
                backgroundColor: brand[600],
                backgroundImage: `linear-gradient(135deg, ${brand[300]} 0%, ${brand[500]} 100%)`,
                boxShadow: `0 16px 28px ${alpha(brand[500], 0.28)}`,
              },
              '&:active': {
                backgroundImage: 'none',
                backgroundColor: brand[600],
                boxShadow: `0 10px 20px ${alpha(brand[600], 0.18)}`,
              },
              ...theme.applyStyles('dark', {
                color: gray[50],
                backgroundColor: brand[500],
                backgroundImage: `linear-gradient(135deg, ${brand[400]} 0%, ${brand[600]} 100%)`,
                boxShadow: `0 14px 30px ${alpha(brand[900], 0.55)}`,
                border: `1px solid ${alpha(brand[300], 0.24)}`,
                '&:hover': {
                  backgroundImage: `linear-gradient(135deg, ${brand[300]} 0%, ${brand[500]} 100%)`,
                  backgroundColor: brand[400],
                  boxShadow: `0 18px 34px ${alpha(brand[900], 0.62)}`,
                },
                '&:active': {
                  backgroundImage: 'none',
                  backgroundColor: brand[600],
                },
              }),
            },
          },
          {
            props: {
              color: 'secondary',
              variant: 'contained',
            },
            style: {
              color: 'white',
              backgroundColor: gray[900],
              backgroundImage: `linear-gradient(135deg, ${gray[700]} 0%, ${gray[900]} 100%)`,
              boxShadow: `0 12px 24px ${alpha(gray[900], 0.18)}`,
              border: `1px solid ${alpha(gray[700], 0.5)}`,
              '&:hover': {
                backgroundColor: gray[800],
                backgroundImage: `linear-gradient(135deg, ${gray[600]} 0%, ${gray[800]} 100%)`,
                boxShadow: `0 16px 28px ${alpha(gray[900], 0.22)}`,
              },
              '&:active': {
                backgroundImage: 'none',
                backgroundColor: gray[800],
              },
              ...theme.applyStyles('dark', {
                border: `1px solid ${alpha(gray[300], 0.24)}`,
                boxShadow: `0 14px 28px ${alpha(gray[900], 0.45)}`,
              }),
            },
          },
          {
            props: {
              variant: 'outlined',
            },
            style: {
              color: (theme.vars || theme).palette.text.primary,
              border: '1px solid',
              borderColor: alpha(brand[500], 0.18),
              backgroundColor: alpha(gray[50], 0.72),
              backdropFilter: 'blur(12px)',
              '&:hover': {
                backgroundColor: alpha(gray[50], 0.92),
                borderColor: alpha(brand[500], 0.34),
              },
              '&:active': {
                backgroundColor: alpha(brand[100], 0.4),
              },
              ...theme.applyStyles('dark', {
                backgroundColor: alpha(gray[800], 0.82),
                borderColor: alpha(brand[300], 0.22),
                '&:hover': {
                  backgroundColor: alpha(gray[800], 0.94),
                  borderColor: alpha(brand[300], 0.38),
                },
                '&:active': {
                  backgroundColor: alpha(brand[900], 0.38),
                },
              }),
            },
          },
          {
            props: {
              color: 'secondary',
              variant: 'outlined',
            },
            style: {
              color: brand[700],
              border: '1px solid',
              borderColor: alpha(brand[500], 0.28),
              backgroundColor: alpha(brand[50], 0.7),
              '&:hover': {
                backgroundColor: alpha(brand[100], 0.95),
                borderColor: brand[400],
              },
              '&:active': {
                backgroundColor: alpha(brand[200], 0.7),
              },
              ...theme.applyStyles('dark', {
                color: brand[50],
                border: '1px solid',
                borderColor: brand[900],
                backgroundColor: alpha(brand[900], 0.3),
                '&:hover': {
                  borderColor: brand[700],
                  backgroundColor: alpha(brand[900], 0.6),
                },
                '&:active': {
                  backgroundColor: alpha(brand[900], 0.5),
                },
              }),
            },
          },
          {
            props: {
              variant: 'text',
            },
            style: {
              color: gray[700],
              '&:hover': {
                backgroundColor: alpha(brand[100], 0.35),
              },
              '&:active': {
                backgroundColor: alpha(brand[100], 0.55),
              },
              ...theme.applyStyles('dark', {
                color: gray[50],
                '&:hover': {
                  backgroundColor: alpha(brand[900], 0.5),
                },
                '&:active': {
                  backgroundColor: alpha(brand[900], 0.7),
                },
              }),
            },
          },
          {
            props: {
              color: 'secondary',
              variant: 'text',
            },
            style: {
              color: brand[700],
              '&:hover': {
                backgroundColor: alpha(brand[100], 0.5),
              },
              '&:active': {
                backgroundColor: alpha(brand[200], 0.7),
              },
              ...theme.applyStyles('dark', {
                color: brand[100],
                '&:hover': {
                  backgroundColor: alpha(brand[900], 0.5),
                },
                '&:active': {
                  backgroundColor: alpha(brand[900], 0.3),
                },
              }),
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: 'none',
        borderRadius: `calc(${(theme.vars || theme).shape.borderRadius}px - 2px)`,
        textTransform: 'none',
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0,
        color: (theme.vars || theme).palette.text.primary,
        border: '1px solid ',
        borderColor: alpha(brand[500], 0.18),
        backgroundColor: alpha(gray[50], 0.72),
        backdropFilter: 'blur(12px)',
        '&:hover': {
          backgroundColor: alpha(gray[50], 0.92),
          borderColor: alpha(brand[500], 0.34),
        },
        '&:active': {
          backgroundColor: alpha(brand[100], 0.4),
        },
        ...theme.applyStyles('dark', {
          backgroundColor: alpha(gray[800], 0.82),
          borderColor: alpha(brand[300], 0.22),
          '&:hover': {
            backgroundColor: alpha(gray[800], 0.94),
            borderColor: alpha(brand[300], 0.38),
          },
          '&:active': {
            backgroundColor: alpha(brand[900], 0.38),
          },
        }),
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              width: '2.25rem',
              height: '2.25rem',
              padding: '0.25rem',
              [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
            },
          },
          {
            props: {
              size: 'medium',
            },
            style: {
              width: '2.5rem',
              height: '2.5rem',
            },
          },
        ],
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: '10px',
        boxShadow: `0 4px 16px ${alpha(gray[400], 0.2)}`,
        [`& .${toggleButtonGroupClasses.selected}`]: {
          color: brand[500],
        },
        ...theme.applyStyles('dark', {
          [`& .${toggleButtonGroupClasses.selected}`]: {
            color: '#fff',
          },
          boxShadow: `0 4px 16px ${alpha(brand[700], 0.5)}`,
        }),
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '12px 16px',
        textTransform: 'none',
        borderRadius: '10px',
        fontWeight: 500,
        ...theme.applyStyles('dark', {
          color: gray[400],
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
          [`&.${toggleButtonClasses.selected}`]: {
            color: brand[300],
          },
        }),
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: '1px solid ',
        borderColor: alpha(gray[300], 0.8),
        boxShadow: '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
        backgroundColor: alpha(gray[100], 0.4),
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': {
          borderColor: brand[300],
        },
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: '2px',
          borderColor: brand[400],
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          '&:hover': {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: '0 0 0 1.5px hsl(210, 0%, 0%) inset',
          backgroundColor: alpha(gray[900], 0.8),
          '&:hover': {
            borderColor: brand[300],
          },
          '&.Mui-focusVisible': {
            borderColor: brand[400],
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
          },
        }),
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: 'none',
      },
      input: ({ theme }) => ({
        fontSize: theme.typography.pxToRem(15),
        lineHeight: 1.35,
        fontWeight: 500,
        '&::placeholder': {
          opacity: 0.9,
          color: gray[500],
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: 0,
      },
      root: ({ theme }) => ({
        padding: '10px 14px',
        color: (theme.vars || theme).palette.text.primary,
        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: `1px solid ${alpha(brand[500], 0.18)}`,
        backgroundColor: alpha(gray[50], 0.84),
        backdropFilter: 'blur(10px)',
        boxShadow: `0 8px 22px ${alpha(gray[900], 0.05)}`,
        transition:
          'border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease, transform 160ms ease',
        '&:hover': {
          borderColor: alpha(brand[500], 0.34),
          backgroundColor: (theme.vars || theme).palette.background.paper,
        },
        [`&.${outlinedInputClasses.focused}`]: {
          outline: `3px solid ${alpha(brand[500], 0.18)}`,
          borderColor: brand[400],
          boxShadow: `0 0 0 4px ${alpha(brand[400], 0.12)}, 0 14px 28px ${alpha(brand[500], 0.12)}`,
        },
        ...theme.applyStyles('dark', {
          backgroundColor: alpha(gray[800], 0.9),
          borderColor: alpha(brand[300], 0.18),
          boxShadow: `0 10px 26px ${alpha(gray[900], 0.32)}`,
          '&:hover': {
            borderColor: alpha(brand[300], 0.4),
            backgroundColor: alpha(gray[800], 0.96),
          },
          [`&.${outlinedInputClasses.focused}`]: {
            borderColor: brand[300],
            boxShadow: `0 0 0 4px ${alpha(brand[300], 0.16)}, 0 16px 30px ${alpha(gray[900], 0.38)}`,
          },
        }),
        variants: [
          {
            props: {
              size: 'small',
            },
            style: {
              minHeight: '2.5rem',
            },
          },
          {
            props: {
              size: 'medium',
            },
            style: {
              minHeight: '2.875rem',
            },
          },
          {
            props: {
              size: 'large-xl',
            },
            style: {
              height: '3rem',
            },
          },
        ],
      }),
      notchedOutline: {
        border: 'none',
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.grey[500],
        ...theme.applyStyles('dark', {
          color: (theme.vars || theme).palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 8,
        color: (theme.vars || theme).palette.text.secondary,
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(14),
        lineHeight: 1.2,
        letterSpacing: 0,
        [`&.Mui-focused`]: {
          color: (theme.vars || theme).palette.text.primary,
        },
      }),
    },
  },
};
